const chapter = {
  id: 'wave-optics',
  slug: 'wave-optics',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Wave Optics',
        hindi: 'तरंग प्रकाशिकी',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 2 प्रश्न',
  },
  syllabusTags: ['Optics & Modern', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'huygens',
      title: {
        english: 'Huygens\' Principle & Wavefronts',
        hindi: 'ह्यूजेन्स का सिद्धांत और वेवफ्रंट्स',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: '**Wavefront** = locus of points oscillating in same phase. Rays are perpendicular to wavefronts. Spherical wavefront from a point source; plane wavefront from a distant source.',
        hindi: '**वेवफ्रंट** = एक ही चरण में दोलन करने वाले बिंदुओं का स्थान। किरणें तरंगाग्रों के लंबवत् होती हैं। एक बिंदु स्रोत से गोलाकार तरंगाग्र; दूर के स्रोत से समतल तरंगाग्र।',
          },
        },
        {
          type: 'definition',
          term: {
            english: 'Huygens\' principle',
        hindi: 'ह्यूजेन्स का सिद्धांत',
          },
          body: {
            english: 'Every point on a wavefront acts as a secondary source emitting wavelets. The new wavefront after time $t$ is the **forward envelope** of these secondary wavelets. Used to derive laws of reflection and Snell\'s law geometrically.',
        hindi: 'तरंगाग्र पर प्रत्येक बिंदु तरंगिकाएं उत्सर्जित करने वाले द्वितीयक स्रोत के रूप में कार्य करता है। समय के बाद नया तरंगफ्रंट $t$ इन द्वितीयक तरंगिकाओं का **आगे का आवरण** है। परावर्तन के नियम और स्नेल के नियम को ज्यामितीय रूप से प्राप्त करने के लिए उपयोग किया जाता है।',
          },
        },
        {
          type: 'note',
          text: {
            english: 'On refraction, **frequency stays the same**, wavelength changes ($\\lambda_{\\text{med}} = \\lambda_0/n$), and so does speed ($v = c/n$).',
        hindi: 'अपवर्तन पर, **आवृत्ति समान रहती है**, तरंग दैर्ध्य बदल जाता है ($\\lambda_{\\text{med}} = \\lambda_0/n$), और गति भी बदल जाती है ($v = c/n$)।',
          },
        },
      ],
    },
    {
      id: 'interference',
      title: {
        english: 'Interference & Young\'s Double-Slit',
        hindi: 'हस्तक्षेप और यंग का डबल-स्लिट',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Sources must be **coherent** (constant phase difference) and have equal frequency. Same polarisation for full contrast.',
        hindi: 'स्रोत **सुसंगत** (निरंतर चरण अंतर) और समान आवृत्ति वाले होने चाहिए। पूर्ण कंट्रास्ट के लिए समान ध्रुवीकरण।',
            },
            {
              english: 'Resultant intensity for two waves: $I = I_1 + I_2 + 2\\sqrt{I_1 I_2}\\cos\\phi$.',
        hindi: 'दो तरंगों के लिए परिणामी तीव्रता: $I = I_1 + I_2 + 2\\sqrt{I_1 I_2}\\cos\\phi$।',
            },
            {
              english: 'For equal amplitudes: $I_{\\max} = 4 I_0$, $I_{\\min} = 0$. **Visibility** $V = (I_{\\max} - I_{\\min})/(I_{\\max} + I_{\\min})$.',
        hindi: 'समान आयामों के लिए: $I_{\\max} = 4 I_0$, $I_{\\min} = 0$। **दृश्यता** $V = (I_{\\max} - I_{\\min})/(I_{\\max} + I_{\\min})$.',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'YDSE — fringe positions & width',
        hindi: 'YDSE - फ्रिंज स्थिति और चौड़ाई',
          },
          statement: {
            english: `Slit separation $d$, screen distance $D$, wavelength $\\lambda$.
**Bright** at $y_n = n\\lambda D/d$ ($n = 0, \\pm 1, \\ldots$).
**Dark** at $y = (2n - 1)\\lambda D/(2d)$.
**Fringe width**: $\\beta = \\lambda D/d$ — same for bright and dark.`,
            hindi: 'स्लिट पृथक्करण $d$, स्क्रीन दूरी $D$, तरंग दैर्ध्य $\\lambda$। **उज्ज्वल** $y_n = n\\lambda D/d$ ($n = 0, \\pm 1, \\ldots$) पर। **अंधेरा** $y = (2n - 1)\\lambda D/(2d)$ पर। **फ्रिंज चौड़ाई**: $\\beta = \\lambda D/d$ - उज्ज्वल और अंधेरे के लिए समान।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Path difference $\\Delta x = d\\sin\\theta \\approx dy/D$. Phase difference $\\phi = (2\\pi/\\lambda)\\Delta x$.',
        hindi: 'पथ अंतर $\\Delta x = d\\sin\\theta \\approx dy/D$. चरण अंतर $\\phi = (2\\pi/\\lambda)\\Delta x$.',
            },
            {
              english: 'Constructive: $\\Delta x = n\\lambda$. Destructive: $\\Delta x = (n - \\tfrac12)\\lambda$.',
        hindi: 'रचनात्मक: $\\Delta x = n\\lambda$. विनाशकारी: $\\Delta x = (n - \\tfrac12)\\lambda$.',
            },
            {
              english: 'Inserting a slab of thickness $t$, refractive index $n$ in front of one slit shifts the entire pattern by $\\Delta y = (n - 1)tD/d$ towards that slit.',
        hindi: 'एक स्लिट के सामने $t$, अपवर्तक सूचकांक $n$ मोटाई का एक स्लैब डालने से पूरा पैटर्न उस स्लिट की ओर $\\Delta y = (n - 1)tD/d$ द्वारा स्थानांतरित हो जाता है।',
            },
            {
              english: 'In a medium of refractive index $n_m$: $\\beta\' = \\beta/n_m$.',
        hindi: 'अपवर्तक सूचकांक $n_m$: $\\beta\\\' = \\beta/n_m$ के माध्यम में।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'YDSE: $\\lambda = 600$ nm, $d = 0.3$ mm, $D = 1$ m. Fringe width?',
        hindi: 'YDSE: $\\lambda = 600$ एनएम, $d = 0.3$ मिमी, $D = 1$ मीटर। फ्रिंज की चौड़ाई?',
          },
          solution: {
            english: '$\\beta = \\lambda D/d = (6{\\times}10^{-7})(1)/(3{\\times}10^{-4}) = 2{\\times}10^{-3}$ m = $2$ mm.',
        hindi: '$\\beta = \\lambda D/d = (6{\\times}10^{-7})(1)/(3{\\times}10^{-4}) = 2{\\times}10^{-3}$ m = $2$ मिमी.',
          },
        },
      ],
    },
    {
      id: 'diffraction',
      title: {
        english: 'Single-Slit Diffraction',
        hindi: 'एकल-रेखाछिद्र विवर्तन',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Single slit minima',
        hindi: 'सिंगल स्लिट मिनिमा',
          },
          statement: {
            english: 'Slit width $a$. Minima at $a\\sin\\theta = n\\lambda$ ($n = \\pm 1, \\pm 2, \\ldots$). Secondary maxima approximately at $a\\sin\\theta = (n + \\tfrac12)\\lambda$.',
        hindi: 'स्लिट की चौड़ाई $a$. मिनिमा $a\\sin\\theta = n\\lambda$ ($n = \\pm 1, \\pm 2, \\ldots$) पर। द्वितीयक अधिकतम सीमा लगभग $a\\sin\\theta = (n + \\tfrac12)\\lambda$ पर।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Central maximum** is brightest and twice as wide as secondary fringes.',
        hindi: '**केंद्रीय अधिकतम** द्वितीयक फ्रिजों की तुलना में सबसे चमकीला और दोगुना चौड़ा है।',
            },
            {
              english: 'Angular width of central max: $2\\lambda/a$. Linear width on screen at distance $D$: $2\\lambda D/a$.',
        hindi: 'केंद्रीय अधिकतम कोणीय चौड़ाई: $2\\lambda/a$। दूरी पर स्क्रीन पर रैखिक चौड़ाई $D$: $2\\lambda D/a$।',
            },
            {
              english: 'Intensity envelope: $I(\\theta) = I_0 [\\sin\\beta/\\beta]^2$ with $\\beta = \\pi a\\sin\\theta/\\lambda$.',
        hindi: 'तीव्रता लिफाफा: $I(\\theta) = I_0 [\\sin\\beta/\\beta]^2$ $\\beta = \\pi a\\sin\\theta/\\lambda$ के साथ। **समाधान सीमा** (रेले): कोणीय पृथक्करण $I(\\theta) = I_0 [\\sin\\beta/\\beta]^2$ (गोलाकार एपर्चर) होने पर दो बिंदु स्रोत हल हो जाते हैं।',
            },
            {
              english: '**Resolving limit** (Rayleigh): two point sources resolved if angular separation $\\ge 1.22\\lambda/a$ (circular aperture).',
        hindi: '**समाधान सीमा** (रेले): कोणीय पृथक्करण $\\ge 1.22\\lambda/a$ (गोलाकार एपर्चर) होने पर दो बिंदु स्रोत हल हो गए।',
            },
          ],
        },
      ],
    },
    {
      id: 'polarisation',
      title: {
        english: 'Polarisation',
        hindi: 'ध्रुवण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Only **transverse** waves can be polarised — confirms light is transverse.',
        hindi: 'केवल **अनुप्रस्थ** तरंगों को ध्रुवीकृत किया जा सकता है - यह पुष्टि करता है कि प्रकाश अनुप्रस्थ है।',
            },
            {
              english: 'Unpolarised light through ideal polariser: intensity $I_0/2$.',
        hindi: 'आदर्श ध्रुवक के माध्यम से अध्रुवित प्रकाश: तीव्रता $I_0/2$।',
            },
            {
              english: '**Malus\' law**: $I = I_0 \\cos^2\\theta$ (between two polarisers at angle $\\theta$).',
        hindi: '**मैलस का नियम**: $I = I_0 \\cos^2\\theta$ (कोण पर दो ध्रुवकों के बीच $\\theta$)।',
            },
            {
              english: '**Brewster\'s law**: at polarising angle $i_p$, reflected light is fully plane-polarised perpendicular to plane of incidence; $\\tan i_p = n$. At this angle, reflected and refracted rays are **perpendicular**.',
        hindi: '**ब्रूस्टर का नियम**: ध्रुवीकरण कोण $i_p$ पर, परावर्तित प्रकाश घटना के तल के लंबवत पूरी तरह से समतल-ध्रुवीकृत होता है; $\\tan i_p = n$. इस कोण पर, परावर्तित और अपवर्तित किरणें **लंबवत** होती हैं।',
            },
            {
              english: 'Other methods: scattering (sky polarisation), double refraction in calcite/quartz.',
        hindi: 'अन्य विधियाँ: प्रकीर्णन (आकाश ध्रुवीकरण), calcite/quartz में दोहरा अपवर्तन।',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Light passes through three polarisers at angles 0°, 45°, 90°. Output intensity?',
        hindi: 'प्रकाश 0°, 45°, 90° कोणों पर तीन ध्रुवकों से होकर गुजरता है। आउटपुट तीव्रता?',
          },
          solution: {
            english: 'After P1: $I_0/2$. After P2 (45°): $(I_0/2)\\cos^2 45° = I_0/4$. After P3 (45° from P2): $(I_0/4)\\cos^2 45° = I_0/8$.',
        hindi: 'P1 के बाद: $I_0/2$. P2 (45°) के बाद: $(I_0/2)\\cos^2 45° = I_0/4$. P3 के बाद (P2 से 45°): $(I_0/4)\\cos^2 45° = I_0/8$।',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\beta = \\lambda D/d',
      desc: {
        english: 'YDSE fringe width',
        hindi: 'YDSE फ्रिंज चौड़ाई',
      },
    },
    {
      latex: 'I = I_1 + I_2 + 2\\sqrt{I_1 I_2}\\cos\\phi',
      desc: {
        english: 'Two-source intensity',
        hindi: 'दो-स्रोत तीव्रता',
      },
    },
    {
      latex: 'a\\sin\\theta = n\\lambda',
      desc: {
        english: 'Single-slit minima',
        hindi: 'सिंगल-स्लिट मिनिमा',
      },
    },
    {
      latex: '2\\lambda/a',
      desc: {
        english: 'Central max angular width',
        hindi: 'केंद्रीय अधिकतम कोणीय चौड़ाई',
      },
    },
    {
      latex: 'I = I_0 \\cos^2\\theta',
      desc: {
        english: 'Malus',
        hindi: 'मालुस',
      },
    },
    {
      latex: '\\tan i_p = n',
      desc: {
        english: 'Brewster',
        hindi: 'ब्रूस्टर',
      },
    },
    {
      latex: 'V = (I_{\\max} - I_{\\min})/(I_{\\max} + I_{\\min})',
      desc: {
        english: 'Visibility',
        hindi: 'दृश्यता',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'YDSE',
      problem: {
        english: 'In YDSE, fringe width is 0.4 mm. If the slit separation is doubled, new fringe width?',
        hindi: 'YDSE में, फ्रिंज की चौड़ाई 0.4 मिमी है। यदि स्लिट पृथक्करण दोगुना हो जाता है, तो नई फ्रिंज चौड़ाई?',
      },
      answer: {
        english: '$0.2$ mm',
        hindi: '$0.2$ मिमी',
      },
      solution: {
        english: '$\\beta \\propto 1/d$.',
        hindi: '$\\beta \\propto 1/d$।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Malus',
      problem: {
        english: 'Unpolarised light intensity $I_0$ passes through two polarisers at 60°. Output?',
        hindi: 'अध्रुवित प्रकाश तीव्रता $I_0$ 60° पर दो ध्रुवकों से होकर गुजरती है। आउटपुट?',
      },
      answer: {
        english: '$I_0/8$',
        hindi: '$I_0/8$',
      },
      solution: {
        english: '$(I_0/2)\\cos^2 60° = (I_0/2)(1/4) = I_0/8$.',
        hindi: '$(I_0/2)\\cos^2 60° = (I_0/2)(1/4) = I_0/8$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Brewster',
      problem: {
        english: 'Polarising angle for glass with $n = \\sqrt 3$?',
        hindi: '$n = \\sqrt 3$ के साथ कांच के लिए ध्रुवीकरण कोण?',
      },
      answer: {
        english: '$60°$',
        hindi: '$60°$',
      },
      solution: {
        english: '$\\tan i_p = \\sqrt 3 \\Rightarrow i_p = 60°$.',
        hindi: '$\\tan i_p = \\sqrt 3 \\Rightarrow i_p = 60°$। स्लिट की चौड़ाई $\\tan i_p = \\sqrt 3 \\Rightarrow i_p = 60°$ मिमी, एनएम, मीटर। केंद्रीय अधिकतम की चौड़ाई?',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Single slit',
      problem: {
        english: 'Slit width $a = 0.1$ mm, $\\lambda = 600$ nm, $D = 1$ m. Width of central max?',
        hindi: 'स्लिट की चौड़ाई $a = 0.1$ मिमी, $\\lambda = 600$ एनएम, $D = 1$ मीटर। केंद्रीय अधिकतम की चौड़ाई?',
      },
      answer: {
        english: '$12$ mm',
        hindi: '$12$ मिमी',
      },
      solution: {
        english: 'Width $= 2\\lambda D/a = 2(6{\\times}10^{-7})(1)/(10^{-4}) = 1.2{\\times}10^{-2}$ m.',
        hindi: 'चौड़ाई $= 2\\lambda D/a = 2(6{\\times}10^{-7})(1)/(10^{-4}) = 1.2{\\times}10^{-2}$ मी.',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'YDSE — slab',
      problem: {
        english: 'A glass slab ($n = 1.5$) of thickness $4\\,\\mu$m introduced in front of one slit. Number of fringes shifted if $\\lambda = 500$ nm?',
        hindi: 'एक स्लिट के सामने $4\\,\\mu$m मोटाई का एक ग्लास स्लैब ($n = 1.5$) लगाया गया है। यदि $\\lambda = 500$ nm हो तो स्थानांतरित फ्रिंजों की संख्या?',
      },
      answer: {
        english: '$4$ fringes',
        hindi: '$4$ किनारे',
      },
      solution: {
        english: 'Shift $= (n - 1)t/\\lambda = 0.5 \\cdot 4{\\times}10^{-6}/(5{\\times}10^{-7}) = 4$.',
        hindi: 'शिफ्ट $= (n - 1)t/\\lambda = 0.5 \\cdot 4{\\times}10^{-6}/(5{\\times}10^{-7}) = 4$.',
      },
    },
  ],
  next: {
    slug: 'dual-nature',
    title: 'Dual Nature of Radiation & Matter',
  },
};

export default chapter;
